/**
 * New operation (04 §2.1, §9, §10): difficulty, the Cadet option with its price stated beside
 * it, and which Hall of Honor veterans ride along. Everything is on one screen with sane
 * defaults, so FORM COMPANY is one Enter away; the choices cannot be changed afterwards.
 */

import './setup.css';
import type { RangerRecord } from '../../meta/types';
import { freshSeed } from './archetypes';
import type { Ctx, Screen } from './context';
import { box, button, el, errorMessage, panel, show } from './dom';
import { availability, DIFFICULTY_NAMES } from './text';
import { choiceRow } from './widgets/choiceRow';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';

/** The career unlock that opens the second veteran slot (04 §5). */
const SECOND_SLOT_UNLOCK = 'veteran-slot-2';

/** What the slider does, in numbers (02 §12), and what a win is worth (04 §2.4). */
function difficultyNote(difficulty: number): string {
  const threat = difficulty === 0 ? 'THREAT RATINGS AS BRIEFED.' : `THREAT RATING +${difficulty} ON EVERY MISSION.`;
  const hall = difficulty >= 2 ? 'TWO SURVIVORS' : 'ONE SURVIVOR';
  return `${threat} ON VICTORY, ${hall} MAY ENTER THE HALL OF HONOR.`;
}

const CADET_LINES = [
  'CADET ALLOWS ONE MISSION RESTART PER THEATRE.',
  'NOBODY FROM A CADET RUN IS INDUCTED INTO THE HALL OF HONOR.',
  'EVERY RECORD FROM THE OPERATION IS MARKED CADET.',
];

/** One operation at a time: the save holds a single run (04 §10). */
function blocked(ctx: Ctx, codename: string): Screen {
  const f = frame('setup', 'NEW OPERATION');
  const toMap = button('TO THE THEATRE MAP', () => ctx.nav.theatre());
  const text = `OPERATION ${codename} IS STILL ACTIVE. WITHDRAW FROM IT ON THE THEATRE MAP BEFORE STARTING ANOTHER.`;
  f.body.append(
    box(
      'menu-col menu-setup-blocked',
      el('div', 'menu-prose menu-paper', text),
      box('menu-row', toMap, button('BACK', () => ctx.nav.title())),
    ),
  );
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => toMap };
}

export function newOperationScreen(ctx: Ctx): Screen {
  const profile = ctx.api.profile();
  const last = profile.operation;
  if (last !== null && last.status === 'active') return blocked(ctx, last.codename);

  const slots = profile.unlocks.includes(SECOND_SLOT_UNLOCK) ? 2 : 1;
  let difficulty = 1;
  let cadet = false;
  const chosen: string[] = [];

  const f = frame('setup', 'NEW OPERATION');
  const slotInfo = el('span', '');
  f.info.append(slotInfo);

  const note = el('div', 'menu-prose menu-paper', difficultyNote(difficulty));
  const difficultyRow = choiceRow(
    'DIFFICULTY',
    DIFFICULTY_NAMES.map((label, value) => ({ value, label })),
    difficulty,
    (value) => {
      difficulty = value;
      note.textContent = difficultyNote(value);
    },
  );
  const level = panel('DIFFICULTY');
  level.body.append(difficultyRow.el, note);

  const cadetRow = choiceRow(
    'CADET',
    [
      { value: false, label: 'OFF' },
      { value: true, label: 'ON' },
    ],
    cadet,
    (value) => (cadet = value),
  );
  const cadetPanel = panel('CADET OPTION');
  cadetPanel.body.append(cadetRow.el, ...CADET_LINES.map((line) => el('div', 'menu-prose menu-dim', line)));

  const error = el('div', 'menu-error menu-hidden');
  const fail = (message: string): void => {
    error.textContent = message;
    show(error, true);
  };

  const vets = panel(`VETERANS - HALL OF HONOR ${profile.hall.length}/6`, 'menu-grow');
  vets.body.append(
    el('div', 'menu-prose menu-paper', 'A VETERAN IS MORTAL LIKE EVERYONE ELSE: BRING YOUR BEST AND YOU CAN LOSE HIM.'),
  );
  const grid = el('div', 'menu-setup-vets');
  const paintSlots = (): void => {
    slotInfo.textContent = `VETERAN SLOTS ${chosen.length}/${slots}`;
  };
  const vetCard = (r: RangerRecord): HTMLElement => {
    const card = dossierCard(r, { describe: ctx.describe, compact: true });
    const state = availability(r);
    if (!state.available) {
      card.append(el('div', 'menu-dossier-reason', state.reason));
      return card;
    }
    const take = button('TAKE ALONG', () => {
      const at = chosen.indexOf(r.id);
      if (at >= 0) chosen.splice(at, 1);
      else if (chosen.length >= slots) return fail(`ALL VETERAN SLOTS ARE FILLED (${slots}/${slots}).`);
      else chosen.push(r.id);
      show(error, false);
      take.setAttribute('aria-pressed', chosen.includes(r.id) ? 'true' : 'false');
      paintSlots();
    }, 'menu-btn-small');
    take.setAttribute('aria-pressed', 'false');
    card.append(box('menu-dossier-actions', take));
    return card;
  };
  for (const r of profile.hall) grid.append(vetCard(r));
  if (profile.hall.length === 0) {
    grid.append(el('div', 'menu-dim menu-prose', 'THE HALL OF HONOR IS EMPTY. WIN AN OPERATION TO FILL IT.'));
  }
  vets.body.append(grid);
  paintSlots();

  const form = button('FORM COMPANY', () => {
    try {
      ctx.api.startOperation(difficulty, freshSeed(), cadet, [...chosen]);
    } catch (err) {
      return fail(errorMessage(err));
    }
    ctx.nav.formCompany();
  });
  const actions = el('div', 'menu-setup-actions');
  if (last !== null) {
    // A finished run keeps its report (and, after a win, its induction) until the next one starts.
    actions.append(button(`LAST OPERATION: ${last.codename}`, () => ctx.nav.operationEnd()));
  }
  actions.append(button('BACK', () => ctx.nav.title()), form);

  const left = box('menu-col menu-setup-left', level.panel, cadetPanel.panel);
  f.body.append(box('menu-col menu-grow menu-setup', box('menu-setup-main', left, vets.panel), error, actions));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => form };
}
